import React, { useEffect, useRef } from 'react';
import {
  FaArrowRight, FaFire, FaHandsHelping, FaBookOpen, FaUserFriends, FaGlobeAfrica, FaStar,
} from 'react-icons/fa';
import './Tcg.css';
import Footer from './Footer';
import imgYouth from '../Assets/youth_experience.jpg';

const JOIN_URL = 'https://thecitychurchluzira.churchcenter.com/people/forms/938180';

const RHYTHMS = [
  { icon: <FaFire aria-hidden="true" />, title: 'Friday Night Worship', when: 'Every Friday, 6:00 PM', desc: 'An electric evening of worship, teaching and fellowship with the family.' },
  { icon: <FaHandsHelping aria-hidden="true" />, title: 'Community Outreach', when: 'Throughout the term', desc: 'Serving our neighbours with practical help and sharing the love of Christ.' },
  { icon: <FaBookOpen aria-hidden="true" />, title: 'Bible Studies', when: 'Midweek small groups', desc: 'Going deeper in the Word together and applying it to everyday life.' },
  { icon: <FaStar aria-hidden="true" />, title: 'Youth Retreats', when: 'Special seasons', desc: 'Getting away for personal growth, connection and spiritual renewal.' },
];

const VALUES = [
  { icon: <FaUserFriends aria-hidden="true" />, title: 'Community', desc: 'A place to belong and be known, no matter where you are starting from.' },
  { icon: <FaBookOpen aria-hidden="true" />, title: 'Discipleship', desc: 'Growing as followers of Jesus who love God and love people.' },
  { icon: <FaGlobeAfrica aria-hidden="true" />, title: 'Mission', desc: 'Being the hands and feet of Christ in our schools, homes and city.' },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const TCGPage = () => {
  const ref = useReveal();

  return (
    <div ref={ref}>
      {/* HERO */}
      <header className="tcg-hero" style={{ '--tcg-hero-img': `url(${imgYouth})` }}>
        <div className="tcc-container tcg-hero__inner">
          <p className="tcc-eyebrow tcg-hero__eyebrow">The Christ Generation</p>
          <h1 className="tcg-hero__title">A generation living for Christ</h1>
          <p className="tcg-hero__lead">
            TCG is a vibrant youth movement at The City Church, living out our faith,
            building real community and making an impact in the world around us.
          </p>
          <div className="tcg-hero__actions">
            <a className="tcc-btn tcc-btn--accent tcc-btn--lg" href={JOIN_URL} target="_blank" rel="noopener noreferrer">
              Join TCG <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="#tcg-rhythms">
              What we do
            </a>
          </div>
        </div>
      </header>

      {/* ABOUT + MISSION */}
      <section className="tcc-section tcg-about reveal" aria-labelledby="tcg-about-h">
        <div className="tcc-container tcg-about__grid">
          <div className="tcg-about__block">
            <p className="tcc-eyebrow">About TCG</p>
            <h2 className="tcc-h2" id="tcg-about-h">Who we are</h2>
            <p>
              The Christ Generation is a youth group focused on living out our faith,
              fostering community and making an impact. We gather regularly to learn,
              grow and support one another as we follow the example of Christ.
            </p>
          </div>
          <div className="tcg-about__block">
            <p className="tcc-eyebrow">Our mission</p>
            <h2 className="tcc-h2">Why we exist</h2>
            <p>
              We are passionate about equipping young people to live out their faith in
              their schools, homes and communities. Through Bible studies, outreach and
              special events, we are empowered to be the hands and feet of Christ.
            </p>
          </div>
        </div>
      </section>

      {/* RHYTHMS */}
      <section id="tcg-rhythms" className="tcc-section tcc-section--surface2 tcg-rhythms reveal" aria-labelledby="tcg-rhythms-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <div className="tcg-head">
            <p className="tcc-eyebrow">Our rhythms</p>
            <h2 className="tcc-h2" id="tcg-rhythms-h">Where the family gathers</h2>
            <p className="tcc-lead">There is always a place for you, whatever week you decide to show up.</p>
          </div>
          <ul className="tcg-rhythms__grid">
            {RHYTHMS.map((r) => (
              <li className="tcg-rhythm tcc-card tcc-card--hover" key={r.title}>
                <span className="tcg-rhythm__icon">{r.icon}</span>
                <div className="tcg-rhythm__body">
                  <h3 className="tcg-rhythm__title">{r.title}</h3>
                  <p className="tcg-rhythm__when">{r.when}</p>
                  <p className="tcg-rhythm__desc">{r.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VALUES */}
      <section className="tcc-section tcg-values reveal" aria-labelledby="tcg-values-h">
        <div className="tcc-container">
          <div className="tcg-head">
            <p className="tcc-eyebrow">What drives us</p>
            <h2 className="tcc-h2" id="tcg-values-h">The heart of TCG</h2>
          </div>
          <ul className="tcg-values__grid">
            {VALUES.map((v) => (
              <li className="tcg-value tcc-card tcc-card--hover" key={v.title}>
                <span className="tcg-value__icon">{v.icon}</span>
                <h3 className="tcg-value__title">{v.title}</h3>
                <p className="tcg-value__desc">{v.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="tcg-cta reveal" aria-labelledby="tcg-cta-h">
        <div className="tcc-container tcg-cta__inner">
          <h2 className="tcg-cta__title" id="tcg-cta-h">Come and belong</h2>
          <p className="tcg-cta__text">
            If you are a young person looking for a place to belong, grow and serve, we
            would love to have you as part of the family. Questions? Our leadership team
            is ready to help.
          </p>
          <div className="tcg-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href={JOIN_URL} target="_blank" rel="noopener noreferrer">
              Join TCG now <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href={JOIN_URL} target="_blank" rel="noopener noreferrer">
              Contact the team
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TCGPage;
